import { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { EntityForm } from './EntityForm'
import { useProject } from '../../context/ProjectContext'
import './EntityModal.css'

/**
 * EntityModal - Full-screen create/edit modal for story entities
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Close handler
 * @param {string} entityType - 'character' | 'location' | 'event' | 'shot'
 * @param {string} mode - 'create' | 'edit'
 * @param {object} initialData - Data to populate form when editing
 * @param {function} onSave - Save handler receives form data
 * @param {function} onDelete - Delete handler (edit mode only)
 * @param {object} originRect - Bounding rect of trigger element for animation origin
 */
export function EntityModal({
  isOpen,
  onClose,
  entityType = 'character',
  mode = 'create',
  initialData = null,
  onSave,
  onDelete,
  originRect = null,
}) {
  const modalRef = useRef(null)
  const previousActiveElement = useRef(null)

  // Get permissions from project context
  const { can } = useProject()
  const canEdit = can('edit')
  const canDelete = can('delete')
  
  // Determine if this is a view-only mode
  const isViewOnly = mode === 'edit' && !canEdit

  // Accent color based on entity type
  const accent = entityType === 'location' || entityType === 'shot' ? 'amber' : 'cyan'

  // Type labels
  const typeLabels = {
    character: 'Character',
    location: 'Location',
    event: 'Event',
    shot: 'Shot',
  }

  // Calculate animation origin from click position
  const getTransformOrigin = () => {
    if (!originRect) return 'center center'
    const x = originRect.left + originRect.width / 2
    const y = originRect.top + originRect.height / 2
    return `${x}px ${y}px`
  }

  // Backdrop variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.2 },
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2, delay: 0.1 },
    },
  }

  // Modal variants with spring animation
  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.9,
      y: 20,
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
        mass: 0.8,
      },
    },
    exit: { 
      opacity: 0,
      scale: 0.95,
      y: 10,
      transition: { duration: 0.15 },
    },
  }

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Focus management
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleKeyDown)
      
      // Focus the modal after animation
      setTimeout(() => {
        modalRef.current?.focus()
      }, 100)
    } else {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
      previousActiveElement.current?.focus()
    }

    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  // Don't render if not open (let AnimatePresence handle exit)
  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className={`entity-modal-backdrop entity-modal-backdrop--${accent}`}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={handleBackdropClick}
        >
          <motion.div
            ref={modalRef}
            className={`entity-modal entity-modal--${accent}`}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ transformOrigin: getTransformOrigin() }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            tabIndex={-1}
          >
            {/* Top highlight */}
            <span className="entity-modal__highlight" />
            
            {/* Header */}
            <header className="entity-modal__header">
              <div className="entity-modal__title-group">
                <span className={`entity-modal__badge entity-modal__badge--${accent}`}>
                  {mode === 'create' ? 'New' : isViewOnly ? 'View' : 'Edit'} {typeLabels[entityType]}
                </span>
                <h2 id="modal-title" className="entity-modal__title">
                  {mode === 'create' 
                    ? `Create ${typeLabels[entityType]}`
                    : initialData?.name || initialData?.title || `${isViewOnly ? 'View' : 'Edit'} ${typeLabels[entityType]}`
                  }
                </h2>
              </div>
              
              <button 
                className="entity-modal__close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path 
                    d="M15 5L5 15M5 5L15 15" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </header>

            {/* Form */}
            <div className="entity-modal__body">
              <EntityForm
                entityType={entityType}
                mode={mode}
                initialData={initialData}
                onSave={onSave}
                onCancel={onClose}
                onDelete={onDelete}
                accent={accent}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  // Render in portal
  return createPortal(modalContent, document.body)
}

export default EntityModal

